import { useState, useEffect } from "react";
import { Form, Icon, Button, Table } from "semantic-ui-react";
import moment from "moment";

const MDL_VIEW_DETAILS = ({ set_mdl_open, data, title, logTime }) => {
  /* VARIABLE DECLARATIONS
	---------------------------------------------------------------------------------*/
  const [description, setDescription] = useState([]);

  /* EVENT LISTENERS
	---------------------------------------------------------------------------------*/
  useEffect(() => {
    try {
      if (data) {
        const flattened = flattenDescription(data);
        setDescription(Array.isArray(flattened) ? flattened : []);
      } else {
        setDescription([]);
      }
    } catch (error) {
      console.error("Error flattening description:", error);
      setDescription([]);
    }
  }, []);
  /* ASYNC FUNCTIONS
	---------------------------------------------------------------------------------*/
  /* EVENT FUNCTIONS
	---------------------------------------------------------------------------------*/
  function closeModal() {
    set_mdl_open(false);
  }

  const formatKey = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  function flattenDescription(description) {
    if (!Array.isArray(description)) {
      return description;
    }

    return description.map((item) => {
      if (typeof item !== "object" || item === null) {
        return item ?? [];
      }
      return flattenNestedStructure(item);
    });
  }

  function flattenNestedStructure(obj) {
    if (typeof obj !== "object" || obj === null) {
      return obj;
    }

    const result = {};

    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          result[key] = [];
        } else if (typeof value[0] === "object") {
          value.forEach((nestedItem) => {
            const flattened = flattenNestedStructure(nestedItem);
            for (const [nestedKey, nestedVal] of Object.entries(flattened)) {
              if (result[nestedKey] !== undefined) {
                if (!Array.isArray(result[nestedKey])) {
                  result[nestedKey] = [result[nestedKey]];
                }
                result[nestedKey].push(nestedVal);
              } else {
                result[nestedKey] = nestedVal;
              }
            }
          });
        } else {
          if (result[key] !== undefined) {
            if (!Array.isArray(result[key])) {
              result[key] = [result[key]];
            }
            result[key].push(...value);
          } else {
            result[key] = value;
          }
        }
      } else if (typeof value === "object" && value !== null) {
        const flattened = flattenNestedStructure(value);
        for (const [nestedKey, nestedVal] of Object.entries(flattened)) {
          if (result[nestedKey] !== undefined) {
            if (!Array.isArray(result[nestedKey])) {
              result[nestedKey] = [result[nestedKey]];
            }
            result[nestedKey].push(nestedVal);
          } else {
            result[nestedKey] = nestedVal;
          }
        }
      } else {
        if (result[key] !== undefined) {
          if (!Array.isArray(result[key])) {
            result[key] = [result[key]];
          }
          result[key].push(value);
        } else {
          result[key] = value;
        }
      }
    }

    return result;
  }

  function formatDate(val) {
    console.log("formatDate - input:", val);
    if (typeof val !== "string") return null;

    const date = new Date(val);
    console.log("formatDate - input:", val, "parsed date:", date, "moment::",moment(val).format("YYYY-MM-DD"));
    const isoLikePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    const spaceDatePattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

    const looksLikeDate =
      !isNaN(date.getTime()) &&
      (isoLikePattern.test(val) || spaceDatePattern.test(val));

    if (!looksLikeDate) return null;

    // Match the YYYY-MM-DD format used for dates across the critical unit
    // tables/modals, instead of a verbose locale-formatted date-time.
    return moment(date).format("LLL");
  }

  // Matches the "Date & Time" column formatting in PortalLogs.js exactly, so
  // the log's own timestamp reads the same here as it does in the list.
  function formatLogTime(val) {
    if (!val) return null;
    const date = new Date(val);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  function formatIfJson(val) {
    if (typeof val !== "string") return null;

    // Quick check if it "looks like" JSON
    if (!(val.trim().startsWith("{") || val.trim().startsWith("["))) {
      return null;
    }

    try {
      let parsed = JSON.parse(val);

      // If it's an array, take the first element
      if (Array.isArray(parsed) && parsed.length > 0) {
        parsed = parsed[0];
      }

      // If it's still not an object, just stringify it
      if (typeof parsed !== "object" || parsed === null) {
        return String(parsed);
      }

      // Convert object entries into "Key: Value" lines
      const formatted = Object.entries(parsed)
        .map(([key, value]) => {
          if (typeof value === "string") {
            value = value.replace(/\s*\n\s*/g, " ");
          }
          return `"${key}": ${value}`;
        })
        .join("\n");

      return (
        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{formatted}</pre>
      );
    } catch {
      return null;
    }
  }

  function renderValue(val) {
    if (val === null || val === "") return "-";

    // date formatting
    const formattedDate = formatDate(val);
    if (formattedDate) return formattedDate;

    // JSON parsing
    const formattedJson = formatIfJson(val);
    if (formattedJson) return formattedJson;
    // Handle arrays
    if (Array.isArray(val)) {
      return val.join("\n");
    }
    // Default
    return String(val);
  }

  /* RENDER APP
	---------------------------------------------------------------------------------*/
  return (
    <>
      <Form>
        <div className="modal-header">
          <div className="modal-header-title">{title}</div>
          <div
            className="modal-header-close"
            onClick={() => set_mdl_open(false)}
          >
            <Icon name="times circle outline" />
          </div>
        </div>
        {formatLogTime(logTime) && (
          <div style={{ padding: "10px 10px 0", color: "#6b7280", fontSize: "13px" }}>
            <strong>Logged At:</strong> {formatLogTime(logTime)}
          </div>
        )}
        <div className="modal-content">
          <Form.Group>
            <div
              style={{
                minHeight: 200,
                maxHeight: 350,
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
                whiteSpace: "pre-wrap",
                overflowY: "auto",
              }}
            >
              {Array.isArray(description) && description.length > 0 ? (
                <Table celled striped unstackable>
                  <Table.Body>
                    {description?.map((obj, i) => {
                      if (!obj || typeof obj !== "object") return null;
                      try {
                        return Object.entries(obj).map(([key, val]) => {
                          if (key === "uuid") {
                            return null;
                          }
                          return (
                            <Table.Row key={`${i}-${key}`}>
                              <Table.Cell width={4}>
                                <strong>
                                  {formatKey ? formatKey(key) : key}
                                </strong>
                              </Table.Cell>
                              <Table.Cell>{renderValue(val)}</Table.Cell>
                            </Table.Row>
                          );
                        });
                      } catch (err) {
                        console.error("Error rendering row:", err);
                        return null;
                      }
                    })}
                  </Table.Body>
                </Table>
              ) : (
                <div className="nodata-placeholder">
                  There is no description to display
                </div>
              )}
            </div>
          </Form.Group>
        </div>
        <div className="modal-footer">
          <Button color="red" onClick={() => closeModal()}>
            Close
          </Button>
        </div>
      </Form>
    </>
  );
};

export default MDL_VIEW_DETAILS;
